import { demoFunc } from './dees-harness-tool-card.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type {
  IHarnessMessageViewState,
  IHarnessSubtaskOpenDetail,
  IHarnessToolCall,
  IHarnessToolDisclosureDetail,
  THarnessContentBlock,
  THarnessMessageStreamDelta,
} from '../interfaces.js';
import {
  DeesHarnessToolRegistry,
  parseMcpName,
  type IHarnessToolDescriptor,
  type IHarnessToolRenderHelpers,
} from '../toolregistry.js';
import { builtinBodyRenderers } from './renderers.js';
import { harnessCardStyles, harnessMonoStyles, harnessControlStyles, harnessActivityStyles } from '../harness.styles.js';
import {
  harnessApplyMessageDelta,
  harnessFormatClockTime,
  harnessFormatDuration,
  harnessToolStatusPresentation,
  harnessTruncate,
  type IHarnessToolStatusPresentation,
} from '../harness.helpers.js';
import { DeesHarnessMessageList } from '../dees-harness-message-list/dees-harness-message-list.js';
import type {
  IDiffViewChangeDetail,
  TDiffView,
} from '../../00group-dataview/dees-dataview-codebox/dees-dataview-codebox.js';
import type { IDiffStats } from '../../00group-dataview/dees-dataview-codebox/codebox.diff.js';
import { DeesHarnessContentBlocks } from '../dees-harness-content-blocks/dees-harness-content-blocks.js';
import '../dees-harness-overflow-text.js';
import '../../00group-dataview/dees-dataview-codebox/dees-dataview-codebox.js';
import './dees-harness-tool-fullscreen.js';
import '../../00group-feedback/dees-badge/dees-badge.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-tool-card': DeesHarnessToolCard;
  }
}

/**
 * Tool call as one transcript row: status rail, glyph, label, MCP badges, the
 * inline request and outcome detail, and the meta (duration, failure token,
 * clock time). The disclosure opens a per-kind body under the row; bodies come
 * from the built-in renderer map or a descriptor's custom renderBody, and kind
 * resolution goes through the tool registry.
 */
@customElement('dees-harness-tool-card')
export class DeesHarnessToolCard extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor call: IHarnessToolCall = { id: '', name: '', status: 'pending' };

  @property({ attribute: false })
  accessor toolRegistry: DeesHarnessToolRegistry = DeesHarnessToolRegistry.default;

  /** Parse markdown during active child streams; disable to parse only at message end. */
  @property({ type: Boolean })
  accessor markdownWhileStreaming: boolean = true;

  /** Clock time in the row meta, following the transcript's `showTimestamps`. */
  @property({ type: Boolean })
  accessor showTimestamp: boolean = false;

  @property({ attribute: false })
  accessor viewState: IHarnessMessageViewState | undefined = undefined;

  @state()
  accessor expanded: boolean | undefined = undefined;

  @state()
  accessor outputExpanded: boolean = false;

  @state()
  accessor bodyMounted: boolean = false;

  /** Body height cap; taller content scrolls in place and offers fullscreen. */
  @property({ type: Number })
  accessor bodyCapPx: number = 360;

  /** Set on the card inside the fullscreen modal: no cap, no maximize bar. */
  @property({ type: Boolean })
  accessor uncapped: boolean = false;

  @state()
  accessor bodyOverflowing: boolean = false;

  @state()
  accessor diffViewOverride: TDiffView | null = null;

  private announcedOpenState: boolean | undefined = undefined;
  private countedDiffStats: { call: IHarnessToolCall; stats: IDiffStats | undefined } | undefined;
  private bodyResizeObserver: ResizeObserver | null = null;
  private observedBodyInner: Element | null = null;
  private fullscreenPortal: HTMLElementTagNameMap['dees-harness-tool-fullscreen'] | null = null;
  private disclosureFrame: number | null = null;
  private returnScrollFrame: number | null = null;
  private terminalTailFollowing = true;
  private terminalScrollHydrated = false;
  private terminalScrollHydrationFrame: number | null = null;
  private terminalTailReturnTimeout: ReturnType<typeof setTimeout> | null = null;
  private terminalTailReturnDelayMs = 5000;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessCardStyles,
    harnessMonoStyles,
    harnessControlStyles,
    harnessActivityStyles,
    css`
      :host {
        display: block;
        container-type: inline-size;
        max-width: 100%;
        font-family: var(--dees-font-family);
      }

      /*
       * A tool call is a line of the transcript, not a card. The shared chrome stays only where
       * the surface still hosts something of its own: the fullscreen body.
       */
      .harness-card:not(.uncapped) {
        border: 0;
        border-radius: 0;
        background: transparent;
      }

      /*
       * The status rail runs the whole card, row and open body alike, on the same left edge the
       * assistant's activity dot uses. It is never the only carrier of status: the glyph is tinted
       * with it and a failed, stopped or non-zero-exit call also spells the outcome out in the meta.
       */
      .rail {
        position: absolute;
        inset: 0 auto 0 0;
        width: 2px;
        border-radius: 1px;
        background: var(--dees-color-border-default);
        pointer-events: none;
      }

      .harness-card.status-running .rail,
      .harness-card.status-pending .rail { background: var(--dees-color-accent-primary); }
      .harness-card.status-error .rail,
      .harness-card.destructive .rail { background: var(--dees-color-accent-error); }
      .harness-card.status-stopped .rail { background: var(--dees-color-text-warning); }

      /*
       * The subagent thread only ever renders inside the fullscreen view, where it takes the height
       * the panel gives it: every box between the card and the nested transcript has to pass that
       * height down, and the clip frame stops being the scroller so the transcript can be one.
       */
      .harness-card.subtaskThreadCard {
        display: flex;
        flex-direction: column;
        min-height: 0;
        height: 100%;
        box-sizing: border-box;
      }

      .harness-card.subtaskThreadCard .bodyDisclosure.open,
      .harness-card.subtaskThreadCard .bodyDisclosureInner,
      .harness-card.subtaskThreadCard .bodyClipFrame,
      .harness-card.subtaskThreadCard .body {
        display: flex;
        flex: 1;
        flex-direction: column;
        min-height: 0;
      }

      .harness-card.subtaskThreadCard .bodyClipFrame {
        max-height: none !important;
        overflow: hidden;
      }

      /* The content edge matches the assistant prose padding, so a row starts where its text does. */
      .header {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        width: 100%;
        min-height: 28px;
        box-sizing: border-box;
        padding: 2px var(--dees-spacing-sm) 2px var(--dees-spacing-md);
        border: 0;
        border-radius: var(--dees-radius-md);
        background: transparent;
        color: inherit;
        cursor: pointer;
        font-family: inherit;
        text-align: left;
      }

      .header:hover {
        background: var(--dees-color-hover);
      }

      @media (pointer: coarse) {
        .header {
          min-height: 44px;
        }
      }

      .toolGlyph {
        flex: 0 0 auto;
        color: var(--dees-color-text-secondary);
      }

      .harness-card.status-running .toolGlyph,
      .harness-card.status-pending .toolGlyph { color: var(--dees-color-accent-primary); }
      .harness-card.status-error .toolGlyph,
      .harness-card.destructive .toolGlyph { color: var(--dees-color-accent-error); }
      .harness-card.status-stopped .toolGlyph { color: var(--dees-color-text-warning); }

      .toolLabel {
        flex: 0 0 auto;
        max-width: 220px;
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--dees-color-text-primary);
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
      }

      .badges {
        display: flex;
        flex: 0 0 auto;
        gap: 4px;
      }

      /* Request and outcome share the one elastic slot; the overflow text keeps the fade reveal. */
      .detail {
        flex: 1;
        min-width: 0;
        font-size: 12px;
      }

      .detailText {
        color: var(--dees-color-text-secondary);
      }

      .resultSummary {
        color: var(--dees-color-text-muted);
        font-family: var(--dees-font-family-mono, monospace);
        font-size: 11px;
      }

      .detailText + .resultSummary::before {
        content: ' · ';
      }

      .metaColumn {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        flex: 0 0 auto;
        color: var(--dees-color-text-muted);
        font-size: 11px;
        font-variant-numeric: tabular-nums;
      }

      .duration {
        white-space: nowrap;
      }

      /* Diff stat of a write, like a git stat: the signs carry it, the colours only confirm. */
      .diffStat {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-weight: 600;
        white-space: nowrap;
      }

      .diffAdded { color: var(--dees-color-text-success); }
      .diffRemoved { color: var(--dees-color-text-error); }

      .statusToken {
        font-weight: 600;
        white-space: nowrap;
      }

      .statusToken.tone-error { color: var(--dees-color-text-error); }
      .statusToken.tone-warning { color: var(--dees-color-text-warning); }

      @container (max-width: 480px) {
        /* Too narrow for one line: the detail drops below, the meta keeps the first line. */
        .header {
          flex-wrap: wrap;
        }
        .detail {
          order: 1;
          flex-basis: 100%;
        }
      }

      .body {
        display: grid;
        /* minmax(0,1fr) keeps wide mono content (split diffs, pre blocks)
           from sizing the track to max-content and pushing children — the
           codebox appbar, pills — past the clip frame; bounded width lets
           inner overflow-x scrollers engage instead. */
        grid-template-columns: minmax(0, 1fr);
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) var(--dees-spacing-md) var(--dees-spacing-sm);
      }

      .kvGrid {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
        gap: 2px var(--dees-spacing-md);
        font-size: var(--dees-font-control-size-sm, 12px);
      }

      .kvKey {
        color: var(--dees-color-text-secondary);
        white-space: nowrap;
      }

      .kvValue {
        color: var(--dees-color-text-primary);
        font-family: var(--dees-font-family-mono);
        overflow-wrap: anywhere;
      }

      .bodyImage {
        max-width: 100%;
        max-height: 420px;
        border-radius: var(--dees-radius-md);
        border: 1px solid var(--dees-color-border-subtle);
      }

      dees-dataview-codebox {
        font-family: var(--dees-font-family);
      }

      /* Three body modes: collapsed (no body), normal (content fits the
         cap), capped (content taller than the cap scrolls inside the frame
         and offers the fullscreen modal). */
      .bodyDisclosure {
        display: grid;
        grid-template-rows: 0fr;
        min-width: 0;
        opacity: 0;
        transform: translateY(-3px);
        transition:
          grid-template-rows var(--dees-transition-default) var(--dees-ease-standard),
          opacity var(--dees-transition-fast) var(--dees-ease-standard),
          transform var(--dees-transition-default) var(--dees-ease-standard);
      }

      .bodyDisclosure.open {
        grid-template-rows: 1fr;
        opacity: 1;
        transform: translateY(0);
      }

      .bodyDisclosureInner {
        min-width: 0;
        min-height: 0;
        overflow: hidden;
      }

      .bodyClipFrame {
        position: relative;
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
      }

      /* The thread fills the fullscreen panel and bleeds to its edges; the list owns the scroll. */
      .subtaskThread {
        display: flex;
        flex: 1;
        flex-direction: column;
        min-width: 0;
        min-height: 0;
        margin-inline: calc(0px - var(--dees-spacing-md));
        overflow: hidden;
      }

      .subtaskThread dees-harness-message-list {
        flex: 1;
        min-height: 0;
      }

      .subtaskUnavailable {
        display: grid;
        place-content: center;
        gap: var(--dees-spacing-xs);
        padding: var(--dees-spacing-md);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-control-size-sm, 12px);
        text-align: center;
      }

      .subtaskNotice {
        padding: var(--dees-spacing-xs) var(--dees-spacing-sm);
        border-bottom: 1px solid var(--dees-color-border-subtle);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
      }

      /*
       * Sits in the body grid next to the payload rather than inside it: "Show all" lifts the
       * card's own character clamp, while this bound belongs to the producer and outlives it, so
       * the two must never read as one control.
       */
      .truncationNotice {
        padding-top: var(--dees-spacing-xs);
        border-top: 1px solid var(--dees-color-border-subtle);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
      }

      .harness-subtask-detail {
        display: grid;
        align-content: start;
        align-self: start;
        gap: 3px;
        min-width: 0;
      }

      .harness-subtask-detail + .harness-subtask-detail {
        padding-top: var(--dees-spacing-sm);
        border-top: 1px solid var(--dees-color-border-subtle);
      }

      .harness-subtask-detail-label {
        color: var(--dees-color-text-muted);
        font-size: 11px;
        font-weight: 600;
      }

      .harness-subtask-detail-text {
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size-sm, 12px);
        line-height: 1.5;
        overflow-wrap: anywhere;
        white-space: pre-wrap;
      }

      .maximizeBar {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--dees-spacing-xs);
        width: 100%;
        padding: var(--dees-spacing-xs) var(--dees-spacing-md);
        border: 0;
        border-top: 1px solid var(--dees-color-border-subtle);
        background: transparent;
        color: var(--dees-color-link);
        cursor: pointer;
        font-family: inherit;
        font-size: var(--dees-font-control-size-sm, 12px);
      }

      .maximizeBar:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-link-hover);
      }

      /* Shell cards read like a terminal: deep background, prompt line,
         mono output. ANSI escapes are stripped by the renderer. */
      .harness-terminal {
        display: grid;
        gap: 0;
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        background: #0c0c0e;
        overflow: hidden;
        font-family: var(--dees-font-family-mono);
        font-size: var(--dees-font-control-size-sm, 12px);
        line-height: 1.5;
      }

      .harness-terminal-command {
        padding: var(--dees-spacing-xs) var(--dees-spacing-md);
        border-bottom: 1px solid var(--dees-color-border-subtle);
        color: #f5f5f7;
        font-weight: 600;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }

      .harness-terminal-command .prompt {
        color: var(--dees-color-accent-success, #4ade80);
        user-select: none;
      }

      .harness-terminal-output {
        margin: 0;
        padding: var(--dees-spacing-xs) var(--dees-spacing-md) var(--dees-spacing-sm);
        max-height: 100%;
        color: #c7c7cc;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }

      .harness-terminal-output.tone-error {
        color: var(--dees-color-text-error);
      }

      .harness-terminal-running {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        padding: var(--dees-spacing-xs) var(--dees-spacing-md) var(--dees-spacing-sm);
        color: #8e8e93;
      }

      .harness-terminal-running .cursor {
        width: 7px;
        height: 14px;
        background: var(--dees-color-accent-primary);
        animation: harness-terminal-blink 1.1s steps(2, start) infinite;
      }

      @keyframes harness-terminal-blink {
        to {
          visibility: hidden;
        }
      }

      .harness-terminal-exit {
        padding: 2px var(--dees-spacing-md) var(--dees-spacing-xs);
        color: var(--dees-color-text-error);
        font-size: var(--dees-font-caption1-size, 11px);
      }

      .harness-terminal-exit.tone-success {
        color: var(--dees-color-accent-success);
      }

      .harness-subtask-open {
        justify-self: start;
        display: inline-flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        padding: 4px 10px;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-md);
        background: transparent;
        color: var(--dees-color-link);
        cursor: pointer;
        font-family: inherit;
        font-size: var(--dees-font-control-size-sm, 12px);
      }

      .harness-subtask-open:hover {
        border-color: var(--dees-color-accent-primary);
        color: var(--dees-color-link-hover);
      }

      @media (prefers-reduced-motion: reduce) {
        .bodyDisclosure {
          transition: none;
        }
      }
    `,
  ];

  private get descriptor(): IHarnessToolDescriptor {
    return this.toolRegistry.resolve(this.enrichedCall);
  }

  /** call with mcp meta derived from the name when the adapter didn't set it */
  private get enrichedCall(): IHarnessToolCall {
    if (this.call.mcp) return this.call;
    const parsed = parseMcpName(this.call.name);
    return parsed ? { ...this.call, mcp: { serverName: parsed.serverName, toolName: parsed.toolName } } : this.call;
  }

  /**
   * Only a reader opens a row. Nothing in the transcript expands or collapses on its own, so a
   * running, failed or stopped call states itself on the collapsed row and a status change never
   * moves what the reader chose to have open.
   */
  public get isOpen(): boolean {
    return this.expanded ?? this.viewState?.toolExpanded ?? false;
  }

  /**
   * Outcome that stands in for the body while the row is collapsed; the detail already carries the
   * request, so this describes what came back.
   */
  private get resultSummary(): string {
    if (!this.descriptor.summary) return '';
    if (
      this.call.status !== 'completed'
      && this.call.status !== 'stopped'
      && !(this.descriptor.kind === 'terminal' && this.call.status === 'error')
    ) return '';
    return this.descriptor.summary(this.enrichedCall).trim();
  }

  /**
   * Counting lines is O(diff), so it happens once per call object rather than once per render; a
   * streaming update replaces that object, which is exactly when the count can change.
   */
  private get diffStats(): IDiffStats | undefined {
    if (this.countedDiffStats?.call !== this.call) {
      this.countedDiffStats = { call: this.call, stats: this.descriptor.diffStats?.(this.enrichedCall) };
    }
    return this.countedDiffStats.stats;
  }

  /**
   * Short word for an outcome the rail colour alone must not have to carry. A truthful non-zero
   * process exit is reported as itself; everything else says what happened to the call.
   */
  private statusToken(
    presentationArg: IHarnessToolStatusPresentation,
  ): { text: string; tone: 'error' | 'warning' } | undefined {
    if (presentationArg.exitCode !== undefined) {
      return { text: `exit ${presentationArg.exitCode}`, tone: 'warning' };
    }
    if (presentationArg.status === 'error') return { text: 'failed', tone: 'error' };
    if (presentationArg.status === 'stopped') return { text: 'stopped', tone: 'warning' };
    return undefined;
  }

  /** A subagent row opens its thread in the fullscreen view instead of a body under the row. */
  private get opensSubtaskThread(): boolean {
    return this.descriptor.kind === 'subtask';
  }

  /** The thread itself exists only in the fullscreen view, and only with a ready projection. */
  private get showsSubtaskThread(): boolean {
    return this.uncapped
      && this.opensSubtaskThread
      && this.call.subtask?.previewState === 'ready';
  }

  public render(): TemplateResult {
    const call = this.enrichedCall;
    const descriptor = this.descriptor;
    const open = this.isOpen;
    const renderBody = this.bodyMounted || open;
    const label = typeof descriptor.label === 'function' ? descriptor.label(call) : descriptor.label;
    const derivedSubtitle = descriptor.subtitle?.(call) ?? '';
    // A producer-supplied title describes the call in its own words — "Read SKILL.md" rather than
    // the sed invocation behind it — so it leads, and the derived line stays as the tooltip.
    const producerTitle = typeof call.title === 'string' ? call.title.trim() : '';
    const subtitle = producerTitle || derivedSubtitle;
    const subtitleTooltip = producerTitle && derivedSubtitle && derivedSubtitle !== producerTitle
      ? `${producerTitle}\n${derivedSubtitle}`
      : subtitle;
    const statusPresentation = harnessToolStatusPresentation(call, descriptor.kind === 'terminal');
    const effectiveStatus = statusPresentation.status;
    const duration = harnessFormatDuration(call.startedAt, call.finishedAt);
    // The body takes the outcome over once the row is open, so the two never state it twice.
    const resultSummary = open ? '' : this.resultSummary;
    const statusToken = this.statusToken(statusPresentation);
    // A subagent row has no body of its own: it opens its thread in the fullscreen view.
    const opensDialog = this.opensSubtaskThread && !this.uncapped;
    return html`
      <div class="harness-card status-${effectiveStatus} ${descriptor.destructive ? 'destructive' : ''} ${this.uncapped ? 'uncapped' : ''} ${this.showsSubtaskThread ? 'subtaskThreadCard' : ''}"
        @mouseenter=${this.cancelTerminalTailReturn}
        @mouseleave=${this.scheduleTerminalTailReturn}
      >
        <span class="rail ${effectiveStatus === 'running' || effectiveStatus === 'pending' ? 'harnessActivity' : ''}" aria-hidden="true"></span>
        <button
          class="header"
          type="button"
          .ariaHasPopup=${opensDialog ? 'dialog' : null}
          .ariaExpanded=${opensDialog ? null : open ? 'true' : 'false'}
          aria-label=${`${label}: ${statusPresentation.label.toLowerCase()}`}
          @click=${this.activateRow}
        >
          <dees-icon class="toolGlyph" .icon=${descriptor.icon} iconSize="14"></dees-icon>
          <span class="toolLabel">${label}</span>
          ${this.renderMcpBadges(call)}
          ${subtitle || resultSummary
            ? html`<dees-harness-overflow-text
                class="detail"
                title=${[subtitleTooltip, resultSummary].filter(Boolean).join('\n')}
              >${subtitle ? html`<span class="detailText">${subtitle}</span>` : ''}${resultSummary
                ? html`<span class="resultSummary">${resultSummary}</span>`
                : ''}</dees-harness-overflow-text>`
            : ''}
          <span class="metaColumn">
            ${this.renderDiffStat()}
            ${duration ? html`<span class="duration">${duration}</span>` : ''}
            ${statusToken
              ? html`<span class="statusToken tone-${statusToken.tone}">${statusToken.text}</span>`
              : ''}
            ${this.showTimestamp && call.startedAt
              ? html`<span class="clock">${harnessFormatClockTime(call.startedAt)}</span>`
              : ''}
          </span>
        </button>
        ${renderBody
          ? html`
              <div
                class=${`bodyDisclosure ${open ? 'open' : ''}`}
                aria-hidden=${String(!open)}
              >
                <div class="bodyDisclosureInner" ?inert=${!open}>
                  <div
                    class="bodyClipFrame"
                    style=${this.uncapped ? '' : `max-height: ${this.bodyCapPx}px;`}
                    @scroll=${this.handleBodyScroll}
                  >
                    <div class="body bodyInner">
                      ${this.renderBody(call)}
                    </div>
                  </div>
                  ${this.bodyOverflowing && !this.uncapped
                    ? html`
                        <button class="maximizeBar" type="button" @click=${this.openFullscreen}>
                          <dees-icon icon="lucide:Maximize2" iconSize="12"></dees-icon>
                          Show everything
                        </button>
                      `
                    : ''}
                </div>
              </div>
            `
          : ''}
      </div>
    `;
  }

  /** A subagent row opens its thread; every other row opens its body in place. */
  private readonly activateRow = (): void => {
    if (this.opensSubtaskThread && !this.uncapped) {
      this.openFullscreen();
      return;
    }
    this.toggleDisclosure();
  };

  private readonly toggleDisclosure = (): void => {
    if (this.isOpen) {
      if (this.disclosureFrame !== null) cancelAnimationFrame(this.disclosureFrame);
      this.disclosureFrame = null;
      this.expanded = false;
      if (this.viewState) this.viewState.toolExpanded = false;
      return;
    }
    if (this.bodyMounted) {
      if (this.disclosureFrame !== null) cancelAnimationFrame(this.disclosureFrame);
      this.disclosureFrame = null;
      this.expanded = true;
      if (this.viewState) this.viewState.toolExpanded = true;
      return;
    }
    this.bodyMounted = true;
    this.expanded = false;
    void this.updateComplete.then(() => {
      if (!this.isConnected || this.expanded !== false) return;
      this.disclosureFrame = requestAnimationFrame(() => {
        this.disclosureFrame = null;
        if (!this.isConnected || this.expanded !== false) return;
        this.shadowRoot?.querySelector<HTMLElement>('.bodyDisclosure')?.getBoundingClientRect();
        this.expanded = true;
        if (this.viewState) this.viewState.toolExpanded = true;
      });
    });
  };

  private readonly openFullscreen = (): void => {
    if (this.fullscreenPortal) return;
    this.dispatchEvent(new CustomEvent('harness-card-retention', {
      detail: { active: true },
      bubbles: true,
      composed: true,
    }));
    // Appended to document.body: content-visibility containment in the
    // transcript would trap a position: fixed layer inside this card.
    const portal = document.createElement('dees-harness-tool-fullscreen');
    portal.call = this.call;
    portal.toolRegistry = this.toolRegistry;
    portal.markdownWhileStreaming = this.markdownWhileStreaming;
    portal.viewState = this.viewState;
    portal.addEventListener('tool-fullscreen-close', this.closeFullscreen, { once: true });
    portal.addEventListener('harness-subtask-open', this.bridgeSubtaskOpen);
    document.body.append(portal);
    this.fullscreenPortal = portal;
  };

  /** The portal hangs off the body, so a drill-in from inside it re-enters the transcript here. */
  private readonly bridgeSubtaskOpen = (eventArg: Event): void => {
    eventArg.stopPropagation();
    this.dispatchEvent(new CustomEvent<IHarnessSubtaskOpenDetail>('harness-subtask-open', {
      detail: (eventArg as CustomEvent<IHarnessSubtaskOpenDetail>).detail,
      bubbles: true,
      composed: true,
    }));
  };

  private readonly closeFullscreen = (): void => {
    const portal = this.fullscreenPortal;
    if (!portal) return;
    this.fullscreenPortal = null;
    portal.remove();
    if (this.returnScrollFrame !== null) cancelAnimationFrame(this.returnScrollFrame);
    // Ancestors keep clipping/containment lifted until the scroll back is on its way.
    this.returnScrollFrame = requestAnimationFrame(() => {
      this.returnScrollFrame = null;
      if (this.isConnected) {
        this.scrollIntoView({ block: 'center', behavior: 'smooth' });
        // The row is what the reader left, so it is what they come back to.
        this.shadowRoot?.querySelector<HTMLButtonElement>('button.header')?.focus();
      }
      this.dispatchEvent(new CustomEvent('harness-card-retention', {
        detail: { active: false },
        bubbles: true,
        composed: true,
      }));
    });
  };

  public willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    super.willUpdate(changedProperties);
    if (changedProperties.has('viewState') || changedProperties.has('call')) {
      this.terminalScrollHydrated = false;
      if (this.terminalScrollHydrationFrame !== null) {
        cancelAnimationFrame(this.terminalScrollHydrationFrame);
        this.terminalScrollHydrationFrame = null;
      }
      if (this.expanded === undefined && this.viewState?.toolExpanded !== undefined) {
        this.expanded = this.viewState.toolExpanded;
      }
      if (!this.outputExpanded && this.viewState?.toolOutputExpanded === true) this.outputExpanded = true;
      if (this.diffViewOverride === null && this.viewState?.toolDiffView !== undefined) {
        this.diffViewOverride = this.viewState.toolDiffView;
      }
      if (this.viewState?.toolTerminalTailFollowing !== undefined) {
        this.terminalTailFollowing = this.viewState.toolTerminalTailFollowing;
      }
      if (this.viewState) {
        const subtask = this.call.subtask;
        // The thread's own view state is kept for as long as there is a thread to reopen.
        if (subtask?.previewState !== 'ready') {
          delete this.viewState.subtaskTranscript;
          delete this.viewState.subtaskTranscriptSessionId;
        } else if (
          this.viewState.subtaskTranscriptSessionId !== subtask.sessionId
          || !this.viewState.subtaskTranscript
        ) {
          this.viewState.subtaskTranscriptSessionId = subtask.sessionId;
          this.viewState.subtaskTranscript = { messages: {}, permissions: {}, questions: {} };
        }
      }
    }
    const previousCall = changedProperties.get('call') as IHarnessToolCall | undefined;
    if (!previousCall || previousCall.id === this.call.id) return;
    this.diffViewOverride = this.viewState?.toolDiffView ?? null;
    this.terminalTailFollowing = this.viewState?.toolTerminalTailFollowing ?? true;
    this.cancelTerminalTailReturn();
  }

  public updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    // The transcript row around this card widens for an open body, and the card's disclosure state
    // is not a property its host can observe, so every change is announced.
    if (this.announcedOpenState !== this.isOpen) {
      this.announcedOpenState = this.isOpen;
      this.dispatchEvent(new CustomEvent<IHarnessToolDisclosureDetail>('harness-tool-disclosure', {
        detail: { open: this.announcedOpenState },
        bubbles: true,
        composed: true,
      }));
    }
    if (!this.isConnected) return;
    this.terminalScrollHydrationFrame ??= requestAnimationFrame(() => {
      this.terminalScrollHydrationFrame = null;
      if (this.isConnected) this.terminalScrollHydrated = true;
    });
    if (this.isOpen && !this.bodyMounted) this.bodyMounted = true;
    if (this.fullscreenPortal) {
      if (changedProperties.has('call')) this.fullscreenPortal.call = this.call;
      if (changedProperties.has('markdownWhileStreaming')) {
        this.fullscreenPortal.markdownWhileStreaming = this.markdownWhileStreaming;
      }
      if (changedProperties.has('viewState')) this.fullscreenPortal.viewState = this.viewState;
    }
    if (!this.isOpen || this.uncapped) {
      this.bodyResizeObserver?.disconnect();
      this.observedBodyInner = null;
      this.bodyOverflowing = false;
      return;
    }
    const bodyInner = this.shadowRoot?.querySelector('.bodyInner') ?? null;
    if (bodyInner === this.observedBodyInner) {
      if (
        (changedProperties.has('call') || changedProperties.has('bodyCapPx'))
        && this.terminalTailFollowing
      ) {
        this.scrollTerminalToTail();
      }
      return;
    }
    this.bodyResizeObserver?.disconnect();
    this.observedBodyInner = bodyInner;
    if (!bodyInner) {
      this.bodyOverflowing = false;
      return;
    }
    this.bodyResizeObserver ??= new ResizeObserver(() => {
      const inner = this.observedBodyInner;
      if (!inner) return;
      // small slack so a few px never trigger the cap chrome
      this.bodyOverflowing = inner.scrollHeight > this.bodyCapPx + 24;
      if (this.terminalTailFollowing) this.scrollTerminalToTail();
    });
    this.bodyResizeObserver.observe(bodyInner);
    if (this.terminalTailFollowing) this.scrollTerminalToTail();
    else if (this.viewState?.toolTerminalScrollTop !== undefined) {
      const frame = this.shadowRoot?.querySelector<HTMLElement>('.bodyClipFrame');
      if (frame) this.setTerminalScrollTop(frame, this.viewState.toolTerminalScrollTop);
    }
  }

  private readonly handleBodyScroll = (eventArg: Event): void => {
    if (this.descriptor.kind !== 'terminal' || this.uncapped) return;
    if (!this.terminalScrollHydrated) return;
    const frame = eventArg.currentTarget as HTMLElement;
    this.terminalTailFollowing = frame.scrollHeight - frame.scrollTop - frame.clientHeight <= 2;
    if (this.viewState) {
      this.viewState.toolTerminalTailFollowing = this.terminalTailFollowing;
      this.viewState.toolTerminalScrollTop = frame.scrollTop;
    }
    if (this.terminalTailFollowing) this.cancelTerminalTailReturn();
  };

  private scrollTerminalToTail(): void {
    if (this.descriptor.kind !== 'terminal' || this.uncapped || !this.isOpen) return;
    const frame = this.shadowRoot?.querySelector<HTMLElement>('.bodyClipFrame');
    if (!frame) return;
    if (this.viewState?.toolTerminalTailFollowing === false) {
      this.terminalTailFollowing = false;
      this.setTerminalScrollTop(frame, this.viewState.toolTerminalScrollTop ?? frame.scrollTop);
      return;
    }
    this.setTerminalScrollTop(frame, frame.scrollHeight);
  }

  private setTerminalScrollTop(frameArg: HTMLElement, scrollTopArg: number): void {
    frameArg.scrollTop = scrollTopArg;
  }

  private readonly scheduleTerminalTailReturn = (): void => {
    if (this.descriptor.kind !== 'terminal' || this.terminalTailFollowing || this.uncapped) return;
    this.cancelTerminalTailReturn();
    this.terminalTailReturnTimeout = setTimeout(() => {
      this.terminalTailReturnTimeout = null;
      this.terminalTailFollowing = true;
      if (this.viewState) this.viewState.toolTerminalTailFollowing = true;
      this.scrollTerminalToTail();
      if (this.viewState) {
        const frame = this.shadowRoot?.querySelector<HTMLElement>('.bodyClipFrame');
        if (frame) this.viewState.toolTerminalScrollTop = frame.scrollTop;
      }
    }, this.terminalTailReturnDelayMs);
  };

  private readonly cancelTerminalTailReturn = (): void => {
    if (!this.terminalTailReturnTimeout) return;
    clearTimeout(this.terminalTailReturnTimeout);
    this.terminalTailReturnTimeout = null;
  };

  private readonly handleDiffViewChange = (eventArg: CustomEvent<IDiffViewChangeDetail>): void => {
    this.diffViewOverride = eventArg.detail.diffView;
    if (this.viewState) this.viewState.toolDiffView = this.diffViewOverride;
  };

  public async connectedCallback(): Promise<void> {
    const connected = super.connectedCallback();
    if (this.hasUpdated) this.requestUpdate();
    await connected;
  }

  public async disconnectedCallback(): Promise<void> {
    // The scroll frame still owes the release, so take it over when it is cancelled below.
    const retainsAncestors = Boolean(this.fullscreenPortal) || this.returnScrollFrame !== null;
    if (this.disclosureFrame !== null) {
      cancelAnimationFrame(this.disclosureFrame);
      this.disclosureFrame = null;
    }
    if (this.returnScrollFrame !== null) {
      cancelAnimationFrame(this.returnScrollFrame);
      this.returnScrollFrame = null;
    }
    if (this.terminalScrollHydrationFrame !== null) {
      cancelAnimationFrame(this.terminalScrollHydrationFrame);
      this.terminalScrollHydrationFrame = null;
    }
    this.terminalScrollHydrated = false;
    this.cancelTerminalTailReturn();
    if (retainsAncestors) {
      this.dispatchEvent(new CustomEvent('harness-card-retention', {
        detail: { active: false },
        bubbles: true,
        composed: true,
      }));
    }
    this.fullscreenPortal?.remove();
    this.fullscreenPortal = null;
    this.bodyResizeObserver?.disconnect();
    this.bodyResizeObserver = null;
    this.observedBodyInner = null;
    await super.disconnectedCallback();
  }

  /**
   * Diff stat of a write, in the row whether it is open or closed. The signs carry the meaning,
   * so the colours are a second cue rather than the only one.
   */
  private renderDiffStat(): TemplateResult | string {
    const stats = this.diffStats;
    if (!stats || (!stats.added && !stats.removed)) return '';
    const label = [
      stats.added ? `${stats.added} lines added` : '',
      stats.removed ? `${stats.removed} lines removed` : '',
    ].filter(Boolean).join(', ');
    return html`
      <span class="diffStat" aria-label=${label}>
        ${stats.added ? html`<span class="diffAdded">+${stats.added}</span>` : ''}
        ${stats.removed ? html`<span class="diffRemoved">−${stats.removed}</span>` : ''}
      </span>
    `;
  }

  private renderMcpBadges(call: IHarnessToolCall): TemplateResult {
    if (!call.mcp) return html``;
    const annotations = call.mcp.annotations;
    return html`
      <span class="badges">
        <dees-badge type="default" .text=${call.mcp.serverName} rounded></dees-badge>
        ${annotations?.readOnlyHint ? html`<dees-badge type="success" text="read-only" rounded></dees-badge>` : ''}
        ${annotations?.destructiveHint ? html`<dees-badge type="error" text="destructive" rounded></dees-badge>` : ''}
        ${annotations?.openWorldHint ? html`<dees-badge type="warning" text="open-world" rounded></dees-badge>` : ''}
        ${annotations?.idempotentHint ? html`<dees-badge type="default" text="idempotent" rounded></dees-badge>` : ''}
      </span>
    `;
  }

  /**
   * Discloses a payload the producer cut to its live transfer budget. The prefix carries no marker
   * of its own — an in-band one would survive into the durable text that replaces it — so the card
   * is the only place the bound can be said, and it says it in words rather than by tone alone.
   */
  private renderTruncationNotice(call: IHarnessToolCall): TemplateResult {
    const notice = call.outputTruncated && call.errorTextTruncated
      ? 'Live preview — the complete output and error arrive with the transcript.'
      : call.errorTextTruncated
        ? 'Live preview — the complete error arrives with the transcript.'
        : call.outputTruncated
          ? 'Live preview — the complete output arrives with the transcript.'
          : '';
    return notice ? html`<div class="truncationNotice">${notice}</div>` : html``;
  }

  private renderBody(call: IHarnessToolCall): TemplateResult {
    const descriptor = this.descriptor;
    const helpers = this.createHelpers();
    const subtask = call.subtask;
    const thread = this.showsSubtaskThread && subtask?.previewState === 'ready' ? subtask : undefined;
    // A running child's own result belongs to its thread, not above it.
    const childBusy = thread?.status.type === 'busy';
    const base = descriptor.renderBody
      ? descriptor.renderBody(call, helpers)
      : (builtinBodyRenderers[descriptor.kind] ?? builtinBodyRenderers.unknown)(
          childBusy ? { ...call, output: undefined, errorText: undefined } : call,
          helpers,
        );
    // The producer's bound is said wherever the call's own payload is shown, directly after it; a
    // running child's thread replaces that payload, so it replaces the notice as well.
    const truncationNotice = childBusy ? html`` : this.renderTruncationNotice(call);
    if (!this.opensSubtaskThread) return html`${base}${truncationNotice}`;
    const subtaskAction = this.renderSubtaskAction(call);
    if (!thread) {
      return html`
        ${base}
        ${truncationNotice}
        ${this.uncapped && subtask
          ? html`
              <div class="subtaskUnavailable">
                <dees-harness-status .status=${subtask.status}></dees-harness-status>
                <span>${subtask.notice ?? (subtask.previewState === 'loading'
                  ? 'Loading the subagent thread…'
                  : 'The thread is unavailable. Open the subagent chat to follow it.')}</span>
              </div>
            `
          : ''}
        ${subtaskAction}
      `;
    }
    return html`
      <section class="subtaskThread" role="region" aria-label="Subagent transcript">
        ${thread.notice || thread.truncated
          ? html`<div class="subtaskNotice">${thread.notice ?? 'Earlier child messages were omitted.'}</div>`
          : ''}
        <dees-harness-message-list
          .messages=${thread.messages}
          .permissions=${thread.permissions ?? []}
          .questions=${thread.questions ?? []}
          .status=${thread.status}
          .toolRegistry=${this.toolRegistry}
          .viewState=${this.viewState?.subtaskTranscript}
          .markdownWhileStreaming=${this.markdownWhileStreaming}
          .autoFollow=${thread.status.type === 'busy'}
          .hideScrollbar=${true}
          .fadeScrollEdges=${true}
          .showRoles=${true}
          .showTimestamps=${false}
          emptyTitle="Waiting for subagent output"
          emptyText="The child session is active."
        >
          <div slot="leading" class="subtaskThreadBrief">${base}${truncationNotice}</div>
        </dees-harness-message-list>
      </section>
      ${subtaskAction}
    `;
  }

  private renderSubtaskAction(callArg: IHarnessToolCall): TemplateResult {
    const childSessionId = callArg.subtask?.sessionId ?? callArg.childSessionId;
    if (!childSessionId) return html``;
    return html`
      <button
        class="harness-subtask-open"
        type="button"
        @click=${() => {
          this.dispatchEvent(new CustomEvent<IHarnessSubtaskOpenDetail>('harness-subtask-open', {
            detail: { sessionId: childSessionId, call: callArg },
            bubbles: true,
            composed: true,
          }));
        }}
      >
        ${(callArg.subtask
          ? callArg.subtask.status.type !== 'busy'
          : callArg.status === 'completed' || callArg.status === 'error' || callArg.status === 'stopped')
          ? 'Open subagent chat'
          : 'Follow subagent live'}
      </button>
    `;
  }

  /**
   * Routes a child delta to the open thread, or to the backing projection when the thread is
   * closed, so opening it later shows the child session as it is now.
   */
  public applySubtaskDelta(delta: THarnessMessageStreamDelta): boolean {
    const subtask = this.call.subtask;
    if (subtask?.previewState !== 'ready') return false;
    if (!subtask.messages.some((message) => message.id === delta.messageId)) return false;
    const list = this.shadowRoot?.querySelector('.subtaskThread > dees-harness-message-list');
    if (list instanceof DeesHarnessMessageList) {
      list.applyDelta(delta);
      return true;
    }
    const threadCard = this.fullscreenPortal?.shadowRoot?.querySelector('dees-harness-tool-card');
    if (threadCard instanceof DeesHarnessToolCard && threadCard.applySubtaskDelta(delta)) return true;
    if (!harnessApplyMessageDelta(subtask.messages, delta)) return false;
    this.requestUpdate();
    return true;
  }

  /** Re-renders this card after its host mutates the shared call object in place. */
  public refreshCall(): void {
    this.requestUpdate();
    const contentBlocks = this.shadowRoot?.querySelector('dees-harness-content-blocks');
    if (contentBlocks instanceof DeesHarnessContentBlocks) contentBlocks.requestUpdate();
    const nestedList = this.shadowRoot?.querySelector('.subtaskThread > dees-harness-message-list');
    if (nestedList instanceof DeesHarnessMessageList) nestedList.refreshMessages();
    if (this.fullscreenPortal) {
      this.fullscreenPortal.requestUpdate();
      const fullscreenCard = this.fullscreenPortal.shadowRoot?.querySelector(
        'dees-harness-tool-card',
      );
      if (fullscreenCard instanceof DeesHarnessToolCard) fullscreenCard.refreshCall();
    }
  }

  private createHelpers(): IHarnessToolRenderHelpers {
    return {
      // The body is the surface these blocks sit on: they bring their own, not a second frame.
      codeBlock: (code, language, filename) => html`
        <dees-dataview-codebox
          .frameless=${true}
          .progLang=${language ?? 'plaintext'}
          .filename=${filename ?? ''}
          .codeToDisplay=${code}
        ></dees-dataview-codebox>
      `,
      diffBlock: (before, after, options) => html`
        <dees-dataview-codebox
          .frameless=${true}
          .progLang=${options?.language ?? 'plaintext'}
          .filename=${options?.filename ?? ''}
          .diffView=${this.diffViewOverride ?? options?.view ?? 'auto'}
          .unifiedDiff=${options?.unifiedDiff ?? ''}
          .showDiffLineNumbers=${options?.showLineNumbers ?? true}
          .codeBefore=${before}
          .codeToDisplay=${after}
          @diff-view-change=${this.handleDiffViewChange}
        ></dees-dataview-codebox>
      `,
      monoBlock: (text, options) => {
        const maxChars = options?.maxChars ?? 6000;
        const { text: shown, truncated } = harnessTruncate(text, this.outputExpanded ? Number.MAX_SAFE_INTEGER : maxChars);
        return html`
          <pre class="harness-mono ${options?.tone === 'error' ? 'tone-error' : ''}">${shown}${truncated ? '…' : ''}</pre>
          ${truncated
            ? html`
                <button
                  class="harness-mono-expand"
                  type="button"
                  @click=${() => {
                    this.outputExpanded = true;
                    if (this.viewState) this.viewState.toolOutputExpanded = true;
                  }}
                >
                  Show all
                </button>
              `
            : ''}
        `;
      },
      keyValues: (pairs) => html`
        <div class="kvGrid">
          ${pairs.map(([key, value]) => html`
            <div class="kvKey">${key}</div>
            <div class="kvValue">${value}</div>
          `)}
        </div>
      `,
      contentBlocks: (blocks: THarnessContentBlock[]) => html`
        <dees-harness-content-blocks .blocks=${blocks}></dees-harness-content-blocks>
      `,
      image: (dataBase64, mimeType, alt) => html`
        <img class="bodyImage" src=${`data:${mimeType};base64,${dataBase64}`} alt=${alt ?? 'tool output image'} />
      `,
    };
  }
}
